import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { IF } from "../../../../../../components/hoc/If";
import { useCurrentOrganisationId } from "../../../../../../services/local/organisationContext/OrganisationContextModel";
import { useFlightPathUser } from "../../../../../auth_insight";
import { AppGridToolbarType } from "../../types/AppGrid_types";
import { AppGridToolbar } from "../base/AppGridToolbar_view";
import { AppGrid } from "../base/AppGrid_view";
import { ModalGridToolbar } from "../base/ModalGridToolbar_view";
import { ImpactsGridModel } from "./ImpactsGridView_model";
import { PositionedSpinner } from "@flightpath/coreui/dist/ui/PositionedSpinner";
import { EntityTypes, GridTypes } from "../../../../../../enums";

interface ImpactsGridProps {
  gridToolbarType?: AppGridToolbarType;
  onAssignSuccess?: (selectedImpacts: number[]) => void;
  selectedLinkItems?: number[];
  data: FP.Entities.IImpactSummary[];
  columnDefs?: FP.Entities.IColumnDef[];
  connectedUsers?: FP.Entities.IUser[];
  isLoading: boolean;
  onCellStateChange?: (
    organisationId: number,
    projectId: number
  ) => (cell: string, isEditMode?: boolean) => Promise<void>;
  preSelectedItemIds?: number[];
  gridType?: EntityTypes | GridTypes;
}

export const ImpactsGrid: React.FunctionComponent<ImpactsGridProps> = observer(
  ({
    gridToolbarType = "base",
    onAssignSuccess,
    data,
    connectedUsers,
    onCellStateChange,
    isLoading,
    columnDefs,
    preSelectedItemIds,
    gridType
  }) => {
    const organisationId = useCurrentOrganisationId();
    const authUser = useFlightPathUser();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const gridRef = useRef<AppGrid>();
    const { projectId } = useParams<{ projectId: string }>();

    const [gridModel, setGridModel] = useState(
      () =>
        new ImpactsGridModel(
          organisationId,
          +projectId,
          authUser,
          columnDefs,
          searchParams,
          gridToolbarType,
          preSelectedItemIds,
          gridType,
          navigate
        )
    );

    useEffect(() => {
      if (gridModel.projectId !== +projectId || gridModel.organisationId !== organisationId) {
        setGridModel(
          new ImpactsGridModel(
            organisationId,
            +projectId,
            authUser,
            columnDefs,
            searchParams,
            gridToolbarType,
            preSelectedItemIds,
            gridType,
            navigate
          )
        );
      }
      // eslint-disable-next-line
    }, [organisationId, projectId]);

    useEffect(() => {
      gridModel.onMount();
      return gridModel.onUnmount;
      // eslint-disable-next-line
    }, []);

    return (
      <>
        <IF condition={isLoading}>
          <PositionedSpinner />
        </IF>
        <IF condition={!isLoading}>
          <IF condition={gridToolbarType === "all" || gridToolbarType === "link-modal"}>
            <ModalGridToolbar
              className="impact-action-grid"
              onQuichSearchInputChange={e => {
                gridRef?.current?.setSearchText(e.currentTarget.value);
              }}
              selectedItems={gridModel.selectedItems}
              preSelectedItemIds={preSelectedItemIds}
              onAssignSuccess={onAssignSuccess}
            />
          </IF>
          <IF condition={gridModel.userCanEditImpacts && (gridToolbarType === "all" || gridToolbarType === "base")}>
            <AppGridToolbar
              connectedUsers={connectedUsers}
              onQuichSearchInputChange={e => {
                gridRef.current.setSearchText(e.currentTarget.value);
              }}
              selectedItemCount={gridModel.selectedItems?.length}
              actions={gridModel.gridActions}
            />
          </IF>
          <AppGrid
            ref={gridRef}
            gridOptions={{ context: { deleteFn: gridModel.deleteFieldData } }}
            filterStoreAddon={gridModel.filterStoreAddon}
            columnStateAddon={gridModel.columnStateAddon}
            textWrapperAddon={gridModel.textWrapAddon}
            onFirstData={gridModel.setSelectedNodes}
            onGridStateUpdate={gridModel.onGridStateUpdate}
            rowData={data}
            columnDefs={gridModel.gridColumns}
            shouldTrackSelectedCell={true}
            connectedUsers={connectedUsers}
            onCellClicked={onCellStateChange && onCellStateChange(organisationId, +projectId)}
            onCellEditModeChange={onCellStateChange && onCellStateChange(organisationId, +projectId)}
            rowSelection="multiple" // Options - allows click selection of rows
          />
        </IF>
      </>
    );
  }
);
